"use client";

/**
 * LoginScreen – light split-card login (desktop) / single-column (mobile).
 *
 * Replaces the former dark login. Warm-paper palette, verdigris accent.
 * Desktop: centered split card (dark welcome panel + light form panel).
 * Mobile: single column with the light form card only.
 *
 * Auth wiring lives in LoginForm (publicAuthClient.signIn.email → redirect "/home").
 * The `email-changed` / `error` params of the email-change confirmation link
 * are rendered by LoginForm and stripped from the URL here.
 */

import { useIsMobile } from "@/app/(dashboard)/_components/task_workspace/inbox/mobile/use-is-mobile";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { type ReactElement, useEffect } from "react";
import "./login.css";
import { useNativeAppKind } from "@/lib/native-app/native-app-context";
import { useTranslations } from "next-intl";
import { DotGridBackdrop } from "../../_components/marketing/dot-grid-backdrop";
import { LoginForm } from "./login-form";
import { AppLoginScreen } from "./login-screen-app";
import { CARD_BG, INK, PAGE_BG } from "./login-shared";
import {
  BrandLockup,
  LoginWelcomePanel,
  TrustLine,
} from "./login-welcome-panel";
import {
  type PortalLoginBranding,
  PortalLoginScreen,
} from "./portal-login-screen";

const FONT_STACK =
  "var(--font-inter), system-ui, -apple-system, 'Segoe UI', sans-serif";

function DemoCta(): ReactElement {
  const t = useTranslations("public.loginScreen");
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
      <span style={{ fontSize: 14, color: "rgba(11,15,25,0.55)" }}>
        {t("keinKonto")}
      </span>
      <Link
        href="/demo"
        className="mklogin-demo-pill"
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: INK,
          textDecoration: "none",
          border: "1px solid rgba(11,15,25,0.16)",
          padding: "9px 16px",
          borderRadius: 10,
          transition: "background .12s",
        }}
      >
        {t("demoBuchen")}
      </Link>
    </div>
  );
}

function DemoFooterLink(): ReactElement {
  const t = useTranslations("public.loginScreen");
  return (
    <div
      style={{
        textAlign: "center",
        fontSize: 13.5,
        color: "rgba(11,15,25,0.6)",
      }}
    >
      {t("keinKonto")}{" "}
      <Link
        href="/demo"
        className="mklogin-link"
        style={{ color: INK, fontWeight: 600, textDecoration: "none" }}
      >
        {t("demoBuchen")}
      </Link>
    </div>
  );
}

function DesktopLogin(): ReactElement {
  const t = useTranslations("public.loginScreen");
  return (
    <div
      className="mklogin"
      style={{
        position: "relative",
        minHeight: "100vh",
        boxSizing: "border-box",
        fontFamily: FONT_STACK,
        background: PAGE_BG,
        color: INK,
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
      }}
    >
      <DotGridBackdrop tone="light" focus="top" />

      <header
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: 1180,
          margin: "0 auto",
          height: 74,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 32px",
          boxSizing: "border-box",
        }}
      >
        <BrandLockup variant="light" />
        <DemoCta />
      </header>

      <main
        style={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "16px 32px 60px",
        }}
      >
        <div
          style={{
            display: "flex",
            width: "100%",
            maxWidth: 1040,
            minHeight: 560,
            borderRadius: 22,
            overflow: "hidden",
            background: CARD_BG,
            border: "1px solid rgba(11,15,25,0.10)",
            boxShadow:
              "0 40px 90px -45px rgba(11,15,25,0.38), 0 14px 34px -18px rgba(11,15,25,0.14)",
          }}
        >
          <LoginWelcomePanel />

          <div
            style={{
              flex: "none",
              width: 444,
              background: CARD_BG,
              padding: "52px 48px",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <h2
              style={{
                fontWeight: 600,
                fontSize: 26,
                letterSpacing: "-0.02em",
                color: INK,
                margin: 0,
              }}
            >
              {t("melde")}
            </h2>

            <LoginForm />

            <div style={{ marginTop: 28 }}>
              <DemoFooterLink />
            </div>
            <div style={{ marginTop: 20 }}>
              <TrustLine />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function MobileLogin(): ReactElement {
  const t = useTranslations("public.loginScreen");
  return (
    <div
      className="mklogin"
      style={{
        width: "100%",
        maxWidth: 420,
        margin: "0 auto",
        fontFamily: FONT_STACK,
        background: PAGE_BG,
        color: INK,
        minHeight: "100dvh",
        paddingBottom: 36,
        boxSizing: "border-box",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <DotGridBackdrop
        tone="light"
        focus="top"
        style={{ inset: "0 0 auto 0", height: 360 }}
      />

      <div
        style={{
          position: "relative",
          zIndex: 1,
          height: 58,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 18px",
        }}
      >
        <BrandLockup variant="light" size={28} />
        <Link
          href="/"
          className="mklogin-website-link"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13.5,
            fontWeight: 500,
            color: "rgba(11,15,25,0.6)",
            textDecoration: "none",
            transition: "color .12s",
          }}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ display: "block" }}
            aria-hidden="true"
          >
            <path d="m12 19-7-7 7-7" />
            <path d="M19 12H5" />
          </svg>
          {t("website")}
        </Link>
      </div>

      <div
        style={{
          position: "relative",
          zIndex: 1,
          margin: "8px 14px 0",
          background: CARD_BG,
          border: "1px solid rgba(11,15,25,0.10)",
          borderRadius: 18,
          boxShadow:
            "0 18px 40px -24px rgba(11,15,25,0.26), 0 4px 12px -7px rgba(11,15,25,0.12)",
          padding: "28px 20px 30px",
        }}
      >
        <h1
          style={{
            fontFamily:
              "var(--font-inter), system-ui, -apple-system, 'Segoe UI', sans-serif",
            fontWeight: 600,
            fontSize: 30,
            lineHeight: 1.09,
            letterSpacing: "-0.035em",
            margin: 0,
          }}
        >
          {t.rich("schoenDassDuWiederDaBist", {
            em: (chunks) => <em style={{ fontStyle: "normal" }}>{chunks}</em>,
          })}
        </h1>
        <p
          style={{
            fontSize: 14,
            lineHeight: 1.55,
            color: "rgba(11,15,25,0.6)",
            margin: "11px 0 0",
          }}
        >
          {t("meldeMachDaWeiter")}
        </p>

        <LoginForm compact />

        <div style={{ marginTop: 24 }}>
          <DemoFooterLink />
        </div>
      </div>

      <div style={{ position: "relative", zIndex: 1, margin: "20px 14px 0" }}>
        <TrustLine />
      </div>
    </div>
  );
}

export function LoginScreen({
  branding,
  portalHost = false,
}: {
  /** Org branding when served on a portal subdomain; null = staff/marketing. */
  readonly branding?: PortalLoginBranding | null;
  /** True on any portalzugang.de host → portal login (never staff marketing). */
  readonly portalHost?: boolean;
}): ReactElement {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isMobile = useIsMobile();
  const nativeApp = useNativeAppKind();

  // Clean the confirmation-link params out of the address bar once LoginForm
  // has read them (it reads on mount, so it always wins this race). `error`
  // goes too — it belongs to that redirect, not to the login page.
  useEffect(() => {
    if (searchParams.get("email-changed") !== "1") return;
    const next = new URLSearchParams(searchParams.toString());
    next.delete("email-changed");
    next.delete("error");
    const qs = next.toString();
    // history-replace: keeps this history entry (redirect or same-screen query)
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  }, [pathname, router, searchParams]);

  if (branding) {
    return <PortalLoginScreen branding={branding} compact={isMobile} />;
  }
  // Bare apex / unknown subdomain on a portal host: neutral portal login
  // (no staff-only "Demo buchen" marketing CTA for tenants).
  if (portalHost) {
    return (
      <PortalLoginScreen
        branding={{ org_name: null, logo_url: null, brand_color: null }}
        compact={isMobile}
      />
    );
  }
  if (nativeApp === "staff") return <AppLoginScreen />;
  return isMobile ? <MobileLogin /> : <DesktopLogin />;
}
